import { useEffect, useRef, useState, type Ref } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '@/auth';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import { ApiError } from '@/api/errors';
import { fetchNoaDocument } from '@/api/endpoints/collections';
import type { CollectionRow, GroundHandlerOption } from '@/types/collection';
import { CollectionHistory } from './CollectionHistory';
import { formatDateTime } from './driverCollectionsData';

/** Reused as-is by `ShipmentDetailScreen`'s per-part NOA download — takes the NOA part id, not a `CollectionRow`, so it doesn't need a driver-collections row to render. */
export function DownloadNoaButton({
  id,
  awb,
  partNumber,
}: {
  id: string;
  awb: string;
  partNumber: number;
}) {
  const t = messages.driverCollections;
  const api = useApi();
  const [state, setState] = useState<'idle' | 'pending' | 'error'>('idle');

  async function handleDownload() {
    setState('pending');
    try {
      const { blob, filename } = await fetchNoaDocument(api, id, awb, partNumber);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch {
      setState('error');
    }
  }

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        onClick={() => void handleDownload()}
        disabled={state === 'pending'}
      >
        {t.downloadNoa}
      </Button>
      {state === 'error' ? (
        <p className="text-destructive mt-1 text-xs">{t.downloadNoaError}</p>
      ) : null}
    </div>
  );
}

export function CollectionCard({
  collection,
  onAction,
  cardRef,
}: {
  collection: CollectionRow;
  onAction: (collection: CollectionRow) => void;
  cardRef?: ((node: HTMLDivElement | null) => void) | undefined;
}) {
  const t = messages.driverCollections;
  const isCollected = collection.status === 'Collected';

  return (
    // tabIndex -1 makes the card a focus target: correcting a collected row
    // unmounts the button that opened the dialog, so Radix has nothing to
    // restore focus to — the screen sends it to the card instead.
    <Card ref={cardRef} tabIndex={-1} className="p-4 outline-none">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">{collection.awb}</h3>
          <p className="text-muted-foreground text-sm">{collection.customerName}</p>
          {collection.partCount > 1 ? (
            <p className="text-muted-foreground text-xs">
              {t.partLabel} {collection.partNumber} {t.ofLabel} {collection.partCount}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          {isCollected ? (
            <Badge variant={collection.hasDeviation ? 'warning' : 'default'}>
              {collection.hasDeviation ? t.deviationBadge : t.collectedBadge}
            </Badge>
          ) : collection.isDelayed ? (
            <Badge variant="warning">{t.delayedBadge}</Badge>
          ) : null}
          {isCollected && collection.wasCorrected ? (
            <Badge variant="outline">{t.correctedBadge}</Badge>
          ) : null}
        </div>
      </div>

      <dl className="mt-3 space-y-2 text-sm">
        <div>
          <dt className="text-muted-foreground text-xs font-medium">{t.handler}</dt>
          <dd>{collection.handlerName ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs font-medium">{t.pickupAddress}</dt>
          <dd>{collection.pickupAddress ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs font-medium">{t.units}</dt>
          <dd>
            {collection.totalUnits ?? '—'} {t.ofLabel} {collection.awbTotalUnits} {collection.unit}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs font-medium">{t.noaReceivedLabel}</dt>
          <dd>{formatDateTime(collection.noaRegisteredAt)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs font-medium">{t.customsClearedAtLabel}</dt>
          <dd>
            {collection.customsClearedAt
              ? formatDateTime(collection.customsClearedAt)
              : t.notCleared}
          </dd>
        </div>
      </dl>

      {!isCollected && collection.collectBy ? (
        <p
          className={
            collection.isDelayed
              ? 'text-warning mt-3 text-sm font-medium'
              : 'text-muted-foreground mt-3 text-sm'
          }
        >
          {t.collectByLabel} {formatDateTime(collection.collectBy)}
          {collection.isDelayed ? ` — ${t.collectByOverdue}` : ''}
        </p>
      ) : null}

      {isCollected ? (
        <>
          <p className="text-muted-foreground mt-3 text-sm">
            {t.collectedPrefix} {collection.collectedUnits} {collection.unit} {t.collectedBy}{' '}
            {collection.collectedBy}, {formatDateTime(collection.collectedAt)}
          </p>
          {collection.wasCorrected && collection.originalCollectedUnits !== null ? (
            <p className="text-muted-foreground text-sm">
              {t.correctedFrom} {collection.originalCollectedUnits} {collection.unit}
            </p>
          ) : null}
        </>
      ) : null}

      <div className="mt-4 flex flex-wrap items-start gap-2">
        {collection.hasNoaDocument ? (
          <DownloadNoaButton
            id={collection.id}
            awb={collection.awb}
            partNumber={collection.partNumber}
          />
        ) : null}
        {isCollected ? (
          <Button variant="outline" onClick={() => onAction(collection)}>
            {t.correctCollection}
          </Button>
        ) : (
          <Button onClick={() => onAction(collection)}>{t.confirmCollection}</Button>
        )}
      </div>

      {isCollected ? (
        <CollectionHistory
          collectionId={collection.id}
          unit={collection.unit}
          eventCount={collection.eventCount}
        />
      ) : null}
    </Card>
  );
}

export function ConfirmCollectionDialog({
  collection,
  mode,
  pending,
  error,
  onClose,
  onSubmitConfirm,
  onSubmitCorrect,
}: {
  collection: CollectionRow;
  mode: 'confirm' | 'correct';
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmitConfirm: (units: number) => void;
  onSubmitCorrect: (units: number, reason: string) => void;
}) {
  const t = messages.collectionConfirm;
  const session = useSession();
  const isCorrect = mode === 'correct';

  // `totalUnits` is null when this part's expected quantity couldn't be read
  // — no default to prefill, so the driver starts from a blank field instead
  // of a spurious `0` or the literal string "null".
  const defaultUnits = isCorrect
    ? (collection.collectedUnits ?? collection.totalUnits)
    : collection.totalUnits;
  const [units, setUnits] = useState(defaultUnits === null ? '' : String(defaultUnits));
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const unitsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = unitsInputRef.current;
    if (!input) return;
    // The quantity is the one thing this dialog exists to capture, so it takes
    // focus, prefilled — select that so a driver typing the actual number
    // replaces it instead of appending to it.
    input.focus();
    input.select();
  }, []);

  const parsedUnits = Number(units);
  const isValidNumber = units.trim() !== '' && Number.isInteger(parsedUnits) && parsedUnits >= 0;
  const sameAsRecorded = isCorrect && isValidNumber && parsedUnits === collection.collectedUnits;
  // No expected quantity to compare against (a legacy/unreadable part) — no
  // deviation is flagged, matching the server-side rule. `totalUnits` is
  // read into a local so TS can narrow it past the null check below.
  const { totalUnits } = collection;
  const isOverExpected = isValidNumber && totalUnits !== null && parsedUnits > totalUnits;
  const hasDeviation =
    isValidNumber && totalUnits !== null && !isOverExpected && parsedUnits !== totalUnits;
  const reasonTrimmed = reason.trim();

  const canSubmit =
    isValidNumber &&
    !sameAsRecorded &&
    (isCorrect ? reasonTrimmed !== '' : acknowledged) &&
    !pending;

  const errorMessage = !error
    ? null
    : error instanceof ApiError && error.status === 409
      ? t.conflictError
      : (error instanceof ApiError && error.problem?.detail) || t.submitError;

  function handleSubmit() {
    if (!canSubmit) return;
    if (isCorrect) onSubmitCorrect(parsedUnits, reasonTrimmed);
    else onSubmitConfirm(parsedUnits);
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isCorrect ? t.correctTitle : t.title}</DialogTitle>
          <DialogDescription>{isCorrect ? t.correctDescription : t.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="border-border/60 bg-secondary/30 rounded-md border p-3">
            <p className="font-medium">{collection.awb}</p>
            <p className="text-muted-foreground">{collection.customerName}</p>
          </div>

          <div>
            <p className="text-muted-foreground text-xs font-medium">{t.readyLabel}</p>
            <p className="mt-0.5">
              {collection.totalUnits ?? t.readyUnknown} {collection.unit}
            </p>
          </div>

          {isCorrect ? (
            <div>
              <p className="text-muted-foreground text-xs font-medium">{t.currentLabel}</p>
              <p className="mt-0.5">
                {collection.collectedUnits} {collection.unit}
              </p>
            </div>
          ) : null}

          <label className="block">
            <span className="text-muted-foreground text-xs font-medium">{t.actualLabel}</span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              aria-describedby={
                hasDeviation || isOverExpected ? 'collection-deviation-warning' : undefined
              }
              className="border-border bg-secondary/40 text-foreground mt-1 h-10 w-full rounded-md border px-3 text-base outline-none"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              ref={unitsInputRef}
            />
          </label>

          {sameAsRecorded ? (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>{t.sameQuantityError}</AlertDescription>
            </Alert>
          ) : isOverExpected ? (
            <Alert variant="warning" id="collection-deviation-warning">
              <TriangleAlert />
              <AlertDescription>{t.overExpectedWarning}</AlertDescription>
            </Alert>
          ) : hasDeviation ? (
            <Alert variant="warning" id="collection-deviation-warning">
              <TriangleAlert />
              <AlertDescription>{t.deviationWarning}</AlertDescription>
            </Alert>
          ) : null}

          {isCorrect ? (
            <label className="block">
              <span className="text-muted-foreground text-xs font-medium">{t.reasonLabel}</span>
              <textarea
                className="border-border bg-secondary/40 text-foreground mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none"
                rows={3}
                placeholder={t.reasonPlaceholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              {reasonTrimmed === '' ? (
                <span className="text-muted-foreground mt-1 block text-xs">{t.reasonRequired}</span>
              ) : null}
            </label>
          ) : (
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>{t.acknowledgement}</span>
            </label>
          )}

          {errorMessage ? (
            <Alert variant="destructive">
              <TriangleAlert />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <p className="text-muted-foreground text-xs">
            {t.recordedAs} {session.displayName} · {session.organisationName ?? '—'} ·{' '}
            {formatDateTime(new Date().toISOString())}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t.cancel}
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {isCorrect ? t.submitCorrection : t.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SectionSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
    </div>
  );
}

export function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = messages.driverCollections;
  return (
    <Alert variant="warning">
      <TriangleAlert />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{message}</span>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.retry}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export function PageHeader({
  headingRef,
  title,
  subtitle,
  count,
}: {
  headingRef?: Ref<HTMLHeadingElement>;
  title: string;
  subtitle: string;
  count: number;
}) {
  return (
    <div className="mb-6">
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-2xl font-semibold tracking-tight outline-none"
      >
        {title}{' '}
        <span aria-hidden="true" className="text-foreground/70 text-base font-normal tabular-nums">
          {count}
        </span>
      </h1>
      <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>
    </div>
  );
}

export function HandlerFilterSelect({
  handlers,
  value,
  onChange,
}: {
  handlers: GroundHandlerOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  const t = messages.driverCollections;

  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
      <label
        htmlFor="handler-filter"
        className="text-muted-foreground text-xs font-medium tracking-wide uppercase"
      >
        {t.handlerFilterLabel}
      </label>
      <select
        id="handler-filter"
        className="border-border bg-secondary/40 h-9 min-w-0 flex-1 rounded-md border px-3 text-sm outline-none sm:flex-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="all">{t.handlerFilterAll}</option>
        {handlers.map((handler) => (
          <option key={handler.id} value={handler.id}>
            {handler.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function DateFilterInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const t = messages.driverCollections;

  return (
    <div className="-mt-3 mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
      <label
        htmlFor="cleared-date-filter"
        className="text-muted-foreground text-xs font-medium tracking-wide uppercase"
      >
        {t.dateFilterLabel}
      </label>
      <input
        id="cleared-date-filter"
        type="date"
        className="border-border bg-secondary/40 h-9 min-w-0 flex-1 rounded-md border px-3 text-sm outline-none sm:flex-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
